import type { MetaFunction } from "react-router";
import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import VideoPlayer from '../components/VideoPlayer';

export const meta: MetaFunction = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": "Mgr. Hana Jarkovská",
      "jobTitle": "Etoped, Psychoterapeut ve výcviku",
      "description": "Etoped a reflektant výcviku v integrativní psychoterapii. Specializace na práci s dětmi, dospívajícími, dospělými i rodinami.",
      "image": "https://www.hanajarkovska.cz/img/profil1.webp",
      "url": "https://www.hanajarkovska.cz/o-mne",
      "telephone": "+420797677397",
      "email": "hana.jarkovska@etoped.cz",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bílovec",
        "postalCode": "743 01",
        "addressCountry": "CZ"
      },
      "knowsAbout": [
        "Psychoterapie",
        "Etopedie",
        "Integrativní psychoterapie",
        "Krizová intervence",
        "Motivační rozhovory",
        "Metoda Knoblocha",
        "Transformační systemický přístup Virginie Satirové"
      ],
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Mgr. - Speciální pedagogika"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Psychoterapeutický výcvik - integrativní psychoterapie (ve výcviku)"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Výcvik Komplexní krizové intervence"
        }
      ]
    }
  };

  return [
    { tagName: "link", rel: "canonical", href: "https://www.hanajarkovska.cz/o-mne" },
    { title: "O mně — Mgr. Hana Jarkovská" },
    { name: "description", content: "Etoped a psychoterapeut v Bílovci. Speciální pedagogika, etopedie a integrativní psychoterapie. Reflektant výcviku, metoda Knoblocha a Satirové." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://www.hanajarkovska.cz/o-mne" },
    { property: "og:title", content: "O mně — Mgr. Hana Jarkovská" },
    { property: "og:description", content: "Etoped a psychoterapeut v Bílovci. Profesní cesta v oblasti speciální pedagogiky, etopedie a integrativní psychoterapie." },
    { property: "og:image", content: "https://www.hanajarkovska.cz/img/profil1.webp" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "O mně — Mgr. Hana Jarkovská" },
    { name: "twitter:description", content: "Etoped a psychoterapeut v Bílovci. Profesní cesta v oblasti speciální pedagogiky, etopedie a integrativní psychoterapie." },
    { name: "twitter:image", content: "https://www.hanajarkovska.cz/img/profil1.webp" },
    { "script:ld+json": structuredData },
  ];
};

export default function AboutPage() {
  return (
    <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
      <Container maxWidth="md">
        <Box>
          <Typography variant="h1" sx={{ mb: 2 }}>
            O mně
          </Typography>

          <Box sx={{ '& > p': { mb: 2.5, textAlign: 'justify' } }}>
            <Typography paragraph>
              Moje profesní cesta nebyla přímočará. Dlouho jsem pracovala v technickém prostředí, kde jsem si vytvořila způsob myšlení a přístup,
              které se později ukázaly jako cenné i v mé současné práci. Později jsem vystudovala Speciální pedagogiku se zaměřením především na{" "}
              <strong>etopedii</strong> a <strong>psychopedii</strong>. Díky shodě okolností jsem dostala příležitost pracovat v{" "}
              <em>Diagnostickém ústavu</em>, kde mám možnost pracovat s
              lidmi s velmi různorodými životními příběhy. Právě příběhy, které si neseme, nás formují a ovlivňují naše vztahy, rozhodování i to,
              jak vnímáme sami sebe.
            </Typography>
          </Box>

          <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 3, mt: 5 }}>
            Vzdělání a přístupy
          </Typography>

          <Box sx={{ '& > p': { mb: 2.5, textAlign: 'justify' } }}>
            <Typography sx={{ textAlign: 'justify' }}>
              Jsem etoped a reflektant výcviku v integrativním <strong>psychoterapeutickém</strong> přístupu, který kombinuje různé terapeutické směry.
              Tento přístup mi umožňuje citlivě reagovat na individuální potřeby klienta a volit techniky podle konkrétní situace.
              Ve své praxi vycházím především z metody Ferdinanda Knoblocha a transformačního systemického přístupu Virginie Satirové.
              Mám také výcvik v <strong>krizové intervenci</strong> a <strong>motivačních rozhovorech</strong>, což mi umožňuje být oporou lidem, kteří procházejí náročným obdobím,
              hledají směr nebo se potýkají s vnitřním konfliktem.
            </Typography>
          </Box>

          <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 3, mt: 5 }}>
            Můj přístup
          </Typography>

          <Box sx={{ '& > p': { mb: 2.5, textAlign: 'justify' } }}>
            <Typography sx={{ textAlign: 'justify' }}>
              Snažím se vytvářet prostor, kde může člověk být takový, jaký právě je, podívat se na svůj život
              jinak a v klidu vnímat sebe i to, co potřebuje. Věřím, že se věci můžou změnit – ale často změna
              přichází jinak, než bychom si představovali.
            </Typography>
          </Box>

          <Box sx={{ mt: 4, overflow: 'hidden' }}>
            <VideoPlayer src="/video/VID_20250513_184126_optimized.mp4" />
          </Box>

          <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 4, mt: 6 }}>
            Odborné vzdělávání a výcviky
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: 'primary.dark' }}>
                  Psychoterapeutické vzdělávání
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, lineHeight: 1.6, fontSize: '0.9rem' } }}>
                  <li>Psychoterapeutický výcvik – integrativní psychoterapie – ve výcviku</li>
                  <li>Kurz Základy neurověd pro psychoterapeuty</li>
                  <li>Výcvik Motivační rozhovory</li>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: 'primary.dark' }}>
                  Etopedie a práce s dětmi
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, lineHeight: 1.6, fontSize: '0.9rem' } }}>
                  <li>Edukativní rodičovská terapie</li>
                  <li>Kurz práce s dětskou kresbou</li>
                  <li>Kurz Test apercepce ruky</li>
                  <li>Kurz Kresba stromu</li>
                  <li>Zvládání agrese, práce s agresivním jedincem</li>
                  <li>Základy canisterapie</li>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: 'primary.dark' }}>
                  Krizová a psychiatrická problematika
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, lineHeight: 1.6, fontSize: '0.9rem' } }}>
                  <li>Výcvik Komplexní krizové intervence</li>
                  <li>Kurz dětská psychiatrie</li>
                  <li>Psychiatrické minimum</li>
                  <li>Sebepoškozování u dětí a mladistvých</li>
                  <li>Metodika prevence sebepoškozování a sebevražd</li>
                  <li>Podpora duševního zdraví v postcovidové době</li>
                  <li>Úzkostné poruchy</li>
                  <li>Zvíře v krizové intervenci</li>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: 'primary.dark' }}>
                  Adiktologie a sociální oblast
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, lineHeight: 1.6, fontSize: '0.9rem' } }}>
                  <li>Stáž – 1. adiktologická klinika</li>
                  <li>Inovativní metody sociální práce s uživateli drog a síťování</li>
                  <li>Prevence relapsu</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
